import type { NormPoint } from '../types';

/**
 * Deterministic point-in-polygon test with explicit edge handling.
 *
 * Uses the ray-casting (even-odd) algorithm. To make results deterministic for
 * points that fall exactly on an edge or vertex, we first run an explicit
 * on-edge test: a point lying on any edge (within `epsilon`) is treated as
 * INSIDE. This removes the classic ambiguity where ray casting flips depending
 * on floating-point rounding at vertices.
 *
 * Polygons are arrays of points; the polygon is implicitly closed (last point
 * connects to first). Coordinates may be in any consistent space.
 */

const DEFAULT_EPS = 1e-9;

function onSegment(p: NormPoint, a: NormPoint, b: NormPoint, eps: number): boolean {
  // Cross product ~ 0 => collinear; then check the point is within the bounding box.
  const cross = (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
  const segLenSq = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  // Scale epsilon by segment length so tolerance is consistent for long/short edges.
  if (Math.abs(cross) > eps * Math.max(1, Math.sqrt(segLenSq))) return false;
  const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
  if (dot < -eps) return false;
  if (dot > segLenSq + eps) return false;
  return true;
}

/** Returns true if `p` lies exactly on the polygon boundary (within eps). */
export function isOnPolygonEdge(
  p: NormPoint,
  polygon: readonly NormPoint[],
  eps: number = DEFAULT_EPS,
): boolean {
  const n = polygon.length;
  if (n < 2) return false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    if (onSegment(p, polygon[j], polygon[i], eps)) return true;
  }
  return false;
}

/**
 * Ray-casting containment test. Points on the boundary count as inside
 * (see module docs). Returns false for degenerate polygons (< 3 vertices).
 */
export function pointInPolygon(
  p: NormPoint,
  polygon: readonly NormPoint[],
  eps: number = DEFAULT_EPS,
): boolean {
  const n = polygon.length;
  if (n < 3) return false;

  if (isOnPolygonEdge(p, polygon, eps)) return true;

  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = polygon[i].y;
    const yj = polygon[j].y;
    const xi = polygon[i].x;
    const xj = polygon[j].x;

    const intersects = yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}
